import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageSquare, AlertCircle, FileCheck, ArrowRight, CheckCircle2, Home, Car } from 'lucide-react'
import axiosInstance from '../../services/axiosInstance'
import PageHeader from '../../components/common/PageHeader'
import './styles.scss'

export default function Tasks() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  
  // Task Categories
  const [unreadMessages, setUnreadMessages] = useState([])
  const [pendingReviews, setPendingReviews] = useState([])
  const [pendingI20s, setPendingI20s] = useState([])
  const [housingRequests, setHousingRequests] = useState([])
  const [pickupRequests, setPickupRequests] = useState([])

  useEffect(() => {
    fetchTasks()
  }, [])

  const fetchTasks = async () => {
    try {
      setLoading(true)
      const response = await axiosInstance.get('/api/applications/admin/all')
      if (response.data.success) {
        const apps = response.data.data
        
        // Categorize tasks
        const msgs = apps.filter(app => app.unreadMessages > 0)
        const reviews = apps.filter(app => app.application.status === 'SUBMITTED')
        const i20s = apps.filter(app => app.application.status === 'ADMITTED')
        const housing = apps.filter(app => app.i20Profile?.accommodationStatus === 'NEEDS_HELP')
        const pickup = apps.filter(app => app.i20Profile?.airportPickupRequested === 'REQUESTED')

        setUnreadMessages(msgs)
        setPendingReviews(reviews)
        setPendingI20s(i20s)
        setHousingRequests(housing)
        setPickupRequests(pickup)
      } else {
        setError(response.data.message || 'Failed to fetch tasks')
      }
    } catch (err) {
      setError(err.message || 'Error connecting to server')
    } finally {
      setLoading(false)
    }
  }

  const handleTaskClick = (appId) => {
    navigate(`/applications/${appId}`)
  }

  const TaskCard = ({ app, icon: Icon, title, description, colorClass }) => (
    <div className="task-card glass-panel" onClick={() => handleTaskClick(app.application.id)}>
      <div className={`task-icon ${colorClass}`}>
        <Icon size={24} />
      </div>
      <div className="task-content">
        <h3 className="task-title">{title}</h3>
        <p className="task-desc">{description}</p>
        <span className="task-meta">{app.studentName} &bull; App #{app.application.applicationNumber}</span>
      </div>
      <div className="task-action">
        <ArrowRight size={20} className="text-muted" />
      </div>
    </div>
  )

  return (
    <div className="tasks-page">
      <PageHeader 
        title="Tasks & Reminders" 
        subtitle="Actionable items that require your attention"
      />

      {loading ? (
        <div className="tasks-loading">Loading tasks...</div>
      ) : error ? (
        <div className="tasks-error">{error}</div>
      ) : (
        <div className="tasks-container">
          
          {/* Unread Messages */}
          <section className="task-section">
            <h2 className="section-title">
              <MessageSquare size={20} className="text-primary" />
              Unread Messages ({unreadMessages.length})
            </h2>
            {unreadMessages.length === 0 ? (
              <div className="empty-state">No unread messages. You're all caught up!</div>
            ) : (
              <div className="task-grid">
                {unreadMessages.map(app => (
                  <TaskCard 
                    key={app.application.id}
                    app={app}
                    icon={MessageSquare}
                    colorClass="icon-blue"
                    title={`${app.unreadMessages} New Message(s)`}
                    description="The student has sent a message that needs a response."
                  />
                ))}
              </div>
            )}
          </section>

          {/* Pending Reviews */}
          <section className="task-section">
            <h2 className="section-title">
              <AlertCircle size={20} className="text-warning" />
              Applications Pending Review ({pendingReviews.length})
            </h2>
            {pendingReviews.length === 0 ? (
              <div className="empty-state">No pending reviews. Great job!</div>
            ) : (
              <div className="task-grid">
                {pendingReviews.map(app => (
                  <TaskCard 
                    key={app.application.id}
                    app={app}
                    icon={FileCheck}
                    colorClass="icon-yellow"
                    title="Needs Initial Review"
                    description="Application submitted and waiting for an admissions decision."
                  />
                ))}
              </div>
            )}
          </section>

          {/* Pending I-20s */}
          <section className="task-section">
            <h2 className="section-title">
              <CheckCircle2 size={20} className="text-success" />
              Pending I-20 Issuance ({pendingI20s.length})
            </h2>
            {pendingI20s.length === 0 ? (
              <div className="empty-state">No I-20s waiting to be issued.</div>
            ) : (
              <div className="task-grid">
                {pendingI20s.map(app => (
                  <TaskCard 
                    key={app.application.id}
                    app={app}
                    icon={FileCheck}
                    colorClass="icon-green"
                    title="Issue I-20 Document"
                    description="Student is admitted. Generate and upload their I-20 document."
                  />
                ))}
              </div>
            )}
          </section>

          {/* Housing Assistance Requests */}
          <section className="task-section">
            <h2 className="section-title" style={{ color: '#d97706' }}>
              <Home size={20} color="#d97706" />
              Housing Assistance Requests ({housingRequests.length})
            </h2>
            {housingRequests.length === 0 ? (
              <div className="empty-state">No housing assistance requests.</div>
            ) : (
              <div className="task-grid">
                {housingRequests.map(app => (
                  <TaskCard 
                    key={app.application.id}
                    app={app}
                    icon={Home}
                    colorClass="icon-yellow"
                    title="Housing Assistance Needed"
                    description="Student requested help securing on-campus or off-campus housing."
                  />
                ))}
              </div>
            )}
          </section>

          {/* Airport Pickup Requests */}
          <section className="task-section">
            <h2 className="section-title" style={{ color: '#059669' }}>
              <Car size={20} color="#059669" />
              Airport Pickup Requests ({pickupRequests.length})
            </h2>
            {pickupRequests.length === 0 ? (
              <div className="empty-state">No airport pickup requests.</div>
            ) : (
              <div className="task-grid">
                {pickupRequests.map(app => (
                  <TaskCard 
                    key={app.application.id}
                    app={app}
                    icon={Car}
                    colorClass="icon-green"
                    title="Airport Pickup Requested"
                    description="Student requested a ride from the airport upon arrival."
                  />
                ))}
              </div>
            )}
          </section>

        </div>
      )}
    </div>
  )
}
